<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import PotentialWinning from '@/components/PotentialWinning.vue'
import { bettingConfig } from '@/config/betting'
import BetAmountInput from '@/components/BetAmountInput.vue'
import ExistingBet from './ExistingBet.vue'
import { useUserStore } from '@/stores/user'
import { userBetsAPI } from '@/services/api'

const props = defineProps({
  selectedOutcome: Object,
  event: Object,
  // new prop to control whether betting is disabled (event finished / in progress)
  isBettingDisabled: Boolean,
})

const emit = defineEmits(['bet-placed', 'bet-error'])

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()

const isAuthenticated = computed(() => userStore.isAuthenticated)
const hasExistingBet = computed(() => {
  return props.event?.user_bets && props.event.user_bets.length > 0
})
const existingBet = computed(() => {
  if (!hasExistingBet.value) return null
  return props.event.user_bets[0]
})

const betAmount = ref(bettingConfig.defaultAmount)
const placingBet = ref(false)

const betAmountError = computed(() => {
  if (!betAmount.value) return null
  const amount = parseFloat(betAmount.value)
  if (isNaN(amount)) {
    return t('betting.validation.mustBeNumber')
  }
  if (amount <= 0) {
    return t('betting.validation.mustBePositive')
  }
  if (amount < bettingConfig.minAmount) {
    return t('betting.validation.minAmount', { min: bettingConfig.minAmount })
  }
  if (amount > bettingConfig.maxAmount) {
    return t('betting.validation.maxAmount', { max: bettingConfig.maxAmount })
  }
  return null
})

const isValidBetAmount = computed(() => {
  return betAmount.value && !betAmountError.value
})

const potentialWinning = computed(() => {
  if (!props.selectedOutcome || !isValidBetAmount.value) return 0
  return (betAmount.value * props.selectedOutcome.price).toFixed(2)
})

const placeBet = async () => {
  if (!userStore.isAuthenticated) {
    router.push({ name: 'login'})
    return
  }
  if (!props.selectedOutcome || !isValidBetAmount.value) {
    return
  }
  placingBet.value = true
  try {
    const betData = {
      amount: parseFloat(betAmount.value),
      outcome_id: props.selectedOutcome.id,
    }
    const response = await userBetsAPI.placeBet(betData)
    if (response.success) {
      emit('bet-placed')
      betAmount.value = bettingConfig.defaultAmount
    } else {
      emit('bet-error', { message: response.error || 'Error betform 001' })
    }
  } catch (error) {
    emit('bet-error', { message: error.response?.data?.message || 'Error betform 002' })
  } finally {
    placingBet.value = false
  }
}

const startCombo = () => {
  userStore.startCombo(getComboItem())
  router.push({ name: 'combo' })
}

const addToCombo = () => {
  userStore.addToCombo(getComboItem())
  router.push({ name: 'combo' })
}

const getComboItem = () => {
  return {
    outcome: props.selectedOutcome,
    // event: {
    //   id: props.event.id,
    //   sport_key: props.event.sport_key,
    //   home_team: props.event.home_team,
    //   away_team: props.event.away_team
    // },
    event: props.event
  }
}

const notInCombo = computed(() => {
  if (!userStore.combo || userStore.combo.length === 0) return true
  return !userStore.combo.some(item => item.event.id === props.event.id)
})

const showComboButton = computed(() => {
  return userStore.isAuthenticated && !props.isBettingDisabled && props.selectedOutcome
})
</script>


<template>
  <div>
    <!-- If user not authenticated show login prompt -->
    <div v-if="!isAuthenticated" class="text-center py-4">
      <v-icon size="48" color="primary" class="mb-4">mdi-account-circle</v-icon>
      <p class="text-subtitle-1 mb-4">{{ $t('events.betting.loginRequired') }}</p>
      <v-btn
        color="primary"
        @click="router.push({ name: 'login'})"
        prepend-icon="mdi-login"
      >
        {{ $t('common.login') }}
      </v-btn>
    </div>

    <!-- Existing bet display -->
    <ExistingBet v-else-if="hasExistingBet" :bet="existingBet" />

    <!-- Event finished or in progress message -->
    <div v-else-if="isBettingDisabled" class="text-center py-4">
      <v-icon 
        size="48" 
        :color="event.score && event.score.completed ? 'success' : 'warning'" 
        class="mb-4"
      >
        {{ event.score && event.score.completed ? 'mdi-check-circle' : 'mdi-clock-alert' }}
      </v-icon>
      <p class="text-subtitle-1 mb-2">
        {{ event.score && event.score.completed ? $t('events.betting.eventFinished') : $t('events.status.inProgress') }}
      </p>
      <p class="text-caption text-medium-emphasis">{{ $t('events.betting.cannotBet') }}</p>
    </div>

    <!-- Betting form -->
    <div v-else>
      <!-- Selected outcome display -->
      <div v-if="selectedOutcome" class="selected-bet mb-4">
        <v-alert
          color="primary"
          variant="outlined"
          class="mb-4"
        >
          <div class="d-flex justify-space-between align-center">
            <div>
              <div class="font-weight-medium">{{ $t('events.betting.selectedBet') }}:</div>
              <div>
                {{ $t(`events.betting.betTypes.${selectedOutcome.market_key}`) }}
              </div>
              <div>
                {{ selectedOutcome.name }}
              </div>
            </div>
            <v-chip color="primary" size="small">
              {{ selectedOutcome.price }}
            </v-chip>
          </div>
        </v-alert>
      </div>

      <div v-if="selectedOutcome" class="mt-2">
        <!-- Bet amount input -->
        <BetAmountInput
          v-model="betAmount"
          :errorMessages="betAmountError"
        />

        <!-- Potential winning display -->
        <PotentialWinning v-if="selectedOutcome && isValidBetAmount"
          :potentialWinning="potentialWinning"
        />

        <!-- Place bet button -->
        <v-btn
          color="success"
          size="large"
          block
          :disabled="!selectedOutcome || !isValidBetAmount"
          :loading="placingBet"
          @click="placeBet"
        >
          <v-icon class="mr-2">mdi-check</v-icon>
          {{ $t('events.betting.placeBet') }}
        </v-btn>
      </div>
    </div>

    <v-alert v-if="!selectedOutcome && !isBettingDisabled" type="info" variant="tonal" class="my-6">
      {{ $t('events.betting.selectToBet') }}
    </v-alert>

    <!-- Combo Button -->
    <div v-if="showComboButton" class="my-6">
      <div v-if="notInCombo">
        <!-- <div class="text-center text-medium-emphasis">
          {{ $t('common.otherwise') }}
        </div> -->
        <v-btn
          color="primary"
          variant="tonal"
          size="large"
          block
          @click="userStore.hasCombo() ? addToCombo() : startCombo()"
        >
          <v-icon class="mr-2">mdi-playlist-plus</v-icon>
          {{ $t(userStore.hasCombo() ? 'events.betting.addToCombo' : 'events.betting.startCombo') }}
        </v-btn>
      </div>

      <div v-else>
        <v-alert type="info" variant="tonal"
          style="cursor: pointer;"
          @click="router.push({ name: 'combo' })"
        >
          {{ $t('combo.eventAlreadySelected') }}
        </v-alert>
      </div>
    </div>

  </div>
</template>

